/**
 * GeometryOperations - Core slicing algorithms
 *
 * Contains the main algorithms for generating slices, slats, and finger joints.
 * This is the "engine" that converts 2D sketches into fabricatable 3D designs.
 *
 * Key algorithms:
 * - generateSlicesAlongPath: Create multiple parallel slices
 * - buildSlicePath: Build slice profile with cap styles
 * - generateSlat: Create single horizontal slat with finger joints
 * - generateSlatSlices: Create multiple slats
 * - generateSlots: Create slots at plane intersections
 *
 * Ported from cc.sketchchair.geometry.GeometryOperations.java
 */

import * as THREE from 'three';
import { Vec2D } from '../sketch/geometry/Vec2D.js';
import { SketchPoint } from '../sketch/SketchPoint.js';
import { SketchPath } from '../sketch/SketchPath.js';
import { SlicePlane } from './SlicePlane.js';
import { SliceSlot } from './SliceSlot.js';
import { SliceType } from './SliceType.js';
import { CapType } from './CapType.js';
import { SliceSlotType } from './SliceSlotType.js';

// Constants
const SCALE = 100; // Scale factor for converting world to screen coordinates
const SLAT_END_SIZE = 50; // Size of fake layers at slat ends

/**
 * Generate parallel slices along a guide path
 *
 * Creates evenly-spaced SlicePlane objects along a guide shape path,
 * with normals perpendicular to the path direction.
 *
 * Algorithm from GeometryOperations.java lines 569-594
 *
 * @param {SketchShape} guideShape - Guide shape to follow
 * @param {CrossSliceSelection} crossSliceSelection - Slice parameters
 * @param {number} start - Start position (0-1)
 * @param {number} end - End position (0-1)
 * @param {number} numSlices - Number of slices to create
 * @returns {Array<SlicePlane>} Array of generated planes
 */
export function generateSlicesAlongPath(guideShape, crossSliceSelection, start, end, numSlices) {
    if (!guideShape || !guideShape.path) {
        console.warn('GeometryOperations.generateSlicesAlongPath: No guide shape path');
        return [];
    }

    const path = guideShape.path;
    const slicePlanes = [];
    const step = numSlices > 1 ? (end - start) / (numSlices - 1) : 0;
    // getPos()/getTangentAt() walk the path by accumulated length, so at
    // exactly t=1 float error can leave them just short and return null -
    // which would silently drop the final rib now that groups default to
    // end=1. Sample a hair inside the end instead.
    const T_MAX = 1 - 1e-5;

    for (let i = 0; i < numSlices; i++) {
        const t = Math.min(start + (i * step), T_MAX);

        // 1. Get position on path at t
        const position2D = path.getPos(t);
        if (!position2D) continue;

        // 2. Get tangent at position t (using finite differences)
        const tangent2D = path.getTangentAt(t);
        if (!tangent2D || (tangent2D.x * tangent2D.x + tangent2D.y * tangent2D.y) < 1e-12) {
            // Degenerate tangent -> the plane normal would be zero and the
            // rib could never intersect anything. Skip this slice.
            console.warn(`generateSlicesAlongPath: degenerate tangent at t=${t.toFixed(3)}, skipping slice`);
            continue;
        }

        // 3. Use tangent directly as normal (this makes green dots correct)
        // NOTE: This makes the plane face along the path instead of perpendicular to it
        // but the intersections work correctly
        const normal2D = tangent2D.clone();

        // 4. Convert to 3D (assuming XY plane at Z=0)
        const scaleFactor = 0.01;
        const position3D = new THREE.Vector3(
            position2D.x * scaleFactor,
            position2D.y * scaleFactor,
            0
        );
        const normal3D = new THREE.Vector3(
            normal2D.x,
            normal2D.y,
            0
        ).normalize();

        // 5. Optional rotation offset (user-configurable)
        if (crossSliceSelection.offsetRotation) {
            const angle = crossSliceSelection.offsetRotation;
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const nx = normal3D.x * cos - normal3D.y * sin;
            const ny = normal3D.x * sin + normal3D.y * cos;
            normal3D.set(nx, ny, 0).normalize();
        }

        // 6. NOTE: flipSide intentionally does NOT flip the plane normal.
        // Flipping the normal reverses the intersection-line ordering in
        // SlicePlaneManager.generateSlots, which then swaps entry/exit a
        // second time for flipSide - the two flips cancelled and "Flip
        // Slots" had no visible effect. Like the Java original, flipSide
        // only swaps which end of the chord each part is slotted from
        // (handled in SlicePlaneManager.generateSlots).

        // 7. Create slice plane
        const slicePlane = new SlicePlane({
            position: position3D,
            normal: normal3D,
            thickness: crossSliceSelection.plane ? crossSliceSelection.plane.thickness : 5.0
        });

        console.log(`Cross-slice ${slicePlanes.length + 1}:`, {
            position: `(${position3D.x.toFixed(2)}, ${position3D.y.toFixed(2)}, ${position3D.z.toFixed(2)})`,
            normal: `(${normal3D.x.toFixed(3)}, ${normal3D.y.toFixed(3)}, ${normal3D.z.toFixed(3)})`,
            tangent2D: `(${tangent2D.x.toFixed(3)}, ${tangent2D.y.toFixed(3)})`,
            normal2D: `(${normal2D.x.toFixed(3)}, ${normal2D.y.toFixed(3)})`
        });

        slicePlane.crossSliceSelection = crossSliceSelection;

        // Note: We don't copy the parent sketch here anymore.
        // Cross-slice profiles are built dynamically based on intersections
        // in SlicePlaneManager.generateSlicesAlongPath()

        slicePlanes.push(slicePlane);
    }

    console.log(`Generated ${slicePlanes.length} slices along path`);
    return slicePlanes;
}

/**
 * Build slice path with cap style
 *
 * Creates the actual 2D profile for a slice by intersecting it with all
 * other planes and applying the selected cap style.
 *
 * Algorithm from GeometryOperations.java buildSlicePath() lines 1200-1500
 *
 * @param {SlicePlane} slicePlane - Slice plane to build path for
 * @param {Array<SlicePlane>} allPlanes - All planes in design
 * @param {CrossSliceSelection} crossSliceSelection - Configuration
 * @returns {SketchPath|null} Built path
 */
export function buildSlicePath(slicePlane, allPlanes, crossSliceSelection) {
    if (!slicePlane || !allPlanes || allPlanes.length === 0) {
        console.warn('GeometryOperations.buildSlicePath: Invalid parameters');
        return null;
    }

    // Get the outline spline to intersect
    let spline;
    if (crossSliceSelection.cropToCurrentShape && slicePlane.sketch) {
        spline = slicePlane.sketch.getFirst ? slicePlane.sketch.getFirst() : null;
    } else if (slicePlane.sketch && slicePlane.sketch.sketchShapes) {
        // Get outer outline of all shapes
        const outerOutline = slicePlane.sketch.sketchShapes.getOuterOutline();
        spline = outerOutline ? outerOutline.getPath() : null;
    }

    if (!spline) {
        console.warn('GeometryOperations.buildSlicePath: No spline found');
        return null;
    }

    // Collect intersection layers (Y positions where other planes cross)
    const layers = [];

    for (const otherPlane of allPlanes) {
        if (otherPlane === slicePlane) continue;
        if (otherPlane.destroy || otherPlane.guide) continue;

        // Get intersection points
        const intersections = slicePlane.getIntersectionWith(otherPlane);

        for (const intersection of intersections) {
            // Store Y position as layer
            layers.push({
                y: intersection.local.y,
                plane: otherPlane
            });
        }
    }

    // Sort layers by Y position
    layers.sort((a, b) => a.y - b.y);

    if (layers.length === 0) {
        console.warn('GeometryOperations.buildSlicePath: No intersection layers found');
        return null;
    }

    // Build top and bottom lines
    const bottomLine = new SketchPath(null);
    const topLine = new SketchPath(null);

    // Add points for each layer
    for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];

        // Calculate X positions (left and right edges)
        // This is simplified - the Java version does complex outline intersection
        const halfWidth = 50; // Placeholder - should calculate from spline

        bottomLine.add(new SketchPoint(i * 10, layer.y));
        topLine.add(new SketchPoint(i * 10, layer.y + halfWidth));
    }

    // Apply cap style
    applyCapStyle(bottomLine, topLine, crossSliceSelection.capType, crossSliceSelection);

    // Apply options
    if (crossSliceSelection.smooth) {
        applySmooth(bottomLine);
        applySmooth(topLine);
    }

    if (crossSliceSelection.generateFlushTops) {
        applyFlushTops(topLine, slicePlane.thickness, SCALE);
    }

    // Combine into single path
    const outline = new SketchPath(null);

    // Add bottom line points
    for (const point of bottomLine.points) {
        outline.add(point.clone());
    }

    // Add top line points (reversed)
    for (let i = topLine.points.length - 1; i >= 0; i--) {
        outline.add(topLine.points[i].clone());
    }

    outline.setClosed(true);

    return outline;
}

/**
 * Generate slots at all plane intersections
 *
 * Finds where planes intersect and creates SliceSlot objects on both
 * planes at each intersection point.
 *
 * Algorithm from GeometryOperations.java buildSlicePath() lines 732-800
 *
 * @param {Array<SlicePlane>} planesToSlice - Planes to generate slots for
 * @param {CrossSliceSelection} crossSliceSelection - Configuration
 */
export function generateSlots(planesToSlice, crossSliceSelection) {
    if (!planesToSlice || planesToSlice.length < 2) {
        return; // Need at least 2 planes to intersect
    }

    console.log(`Generating slots for ${planesToSlice.length} planes`);

    // For each plane
    for (let i = 0; i < planesToSlice.length; i++) {
        const currentPlane = planesToSlice[i];

        // Find intersections with all other planes
        for (let j = 0; j < planesToSlice.length; j++) {
            if (i === j) continue; // Skip self

            const otherPlane = planesToSlice[j];

            // Get intersection points
            const intersections = currentPlane.getIntersectionWith(otherPlane);

            if (intersections.length === 0) {
                continue; // No intersection
            }

            // Create slot at each intersection
            for (const intersection of intersections) {
                // Calculate slot length (height of profile at intersection)
                const slotLength = calculateSlotLength(currentPlane, otherPlane, intersection);

                // Determine slot direction
                let slotDir = intersection.direction;

                // Apply flip if needed
                if (crossSliceSelection && crossSliceSelection.flipSide) {
                    slotDir = new Vec2D(-slotDir.x, -slotDir.y);
                }

                // Create slot
                const slot = new SliceSlot({
                    plane: currentPlane,
                    position: intersection.local,
                    direction: slotDir,
                    length: slotLength,
                    fingerLength: slotLength,
                    width: otherPlane.thickness,
                    numSlots: 1,
                    startOnSlot: false,
                    type: SliceSlotType.SLOT,
                    onEdge: false,
                    percent: 0
                });

                currentPlane.slots.add(slot);
            }
        }

        // Cleanup invalid slots
        if (currentPlane.sketch) {
            const outline = currentPlane.sketch.getOutline ? currentPlane.sketch.getOutline() : null;
            if (outline) {
                currentPlane.slots.cleanup(outline);
            }
        }
    }

    console.log('Slot generation complete');
}

/**
 * Calculate slot length at intersection
 *
 * @param {SlicePlane} plane - Plane receiving the slot
 * @param {SlicePlane} otherPlane - Intersecting plane
 * @param {Object} intersection - Intersection data
 * @returns {number} Slot length
 * @private
 */
function calculateSlotLength(plane, otherPlane, intersection) {
    // Default: use profile height
    if (plane.profileHeight > 0) {
        return plane.profileHeight;
    }

    // Fallback: use a reasonable default based on material thickness
    return otherPlane.thickness * 10;
}

/**
 * Generate a single slat with finger joints
 *
 * Creates a horizontal support piece that connects vertical slices
 * using alternating finger joints.
 *
 * Algorithm from GeometryOperations.java generateSlat() lines 51-239
 *
 * @param {Array<SlicePlane>} planesToSlice - Vertical planes to connect
 * @param {CrossSliceSelection} crossSliceSelection - Configuration
 * @param {number} position - Position along guide (0-1)
 * @returns {SlicePlane|null} Generated slat plane
 */
export function generateSlat(planesToSlice, crossSliceSelection, position) {
    if (!planesToSlice || planesToSlice.length < 2) {
        console.warn('GeometryOperations.generateSlat: Need at least 2 planes');
        return null;
    }

    // Find min/max X extents
    let minX = Infinity;
    let maxX = -Infinity;

    for (const plane of planesToSlice) {
        // Get plane X position (simplified - should project onto slat direction)
        const x = plane.position.x;
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
    }

    const width = maxX - minX;
    const height = crossSliceSelection.slatHeight || 100;

    // Create rectangular slat shape
    const slatPath = new SketchPath(null);
    slatPath.add(new SketchPoint(minX, 0));
    slatPath.add(new SketchPoint(maxX, 0));
    slatPath.add(new SketchPoint(maxX, height));
    slatPath.add(new SketchPoint(minX, height));
    slatPath.setClosed(true);

    // Create slat plane (horizontal)
    const slatPlane = new SlicePlane({
        position: new THREE.Vector3((minX + maxX) / 2, 0, position * 100),
        normal: new THREE.Vector3(0, 0, 1), // Horizontal plane
        thickness: crossSliceSelection.plane ? crossSliceSelection.plane.thickness : 5.0
    });

    // Add finger joints to each vertical layer
    const teethCount = crossSliceSelection.teethCount || 5;
    const slotLength = height / teethCount;
    const fingerLength = slotLength;

    for (const verticalPlane of planesToSlice) {
        const x = verticalPlane.position.x;

        // Add slot to vertical layer
        const layerSlot = new SliceSlot({
            plane: verticalPlane,
            position: new Vec2D(x - minX, 0),
            direction: new Vec2D(0, 1),
            length: slotLength,
            fingerLength: fingerLength,
            width: slatPlane.thickness,
            numSlots: teethCount,
            startOnSlot: false,
            type: SliceSlotType.SLOTS_AND_FINGER
        });

        verticalPlane.slots.add(layerSlot);

        // Add reciprocal slot to slat (becomes fingers on slat)
        const slatSlot = new SliceSlot({
            plane: slatPlane,
            position: new Vec2D(x - minX, 0),
            direction: new Vec2D(0, 1),
            length: slotLength,
            fingerLength: fingerLength,
            width: verticalPlane.thickness,
            numSlots: teethCount,
            startOnSlot: true, // Opposite of layer
            type: SliceSlotType.SLOTS_AND_FINGER
        });

        slatPlane.slots.add(slatSlot);
    }

    console.log('Generated slat with finger joints');
    return slatPlane;
}

/**
 * Generate multiple slats with spacing
 *
 * Algorithm from GeometryOperations.java generateSlatSlices() lines 241-474
 *
 * @param {Array<SlicePlane>} planesToSlice - Vertical planes to connect
 * @param {CrossSliceSelection} crossSliceSelection - Configuration
 * @param {number} start - Start position (0-1)
 * @param {number} end - End position (0-1)
 * @param {number} numSlats - Number of slats
 * @returns {Array<SlicePlane>} Generated slat planes
 */
export function generateSlatSlices(planesToSlice, crossSliceSelection, start, end, numSlats) {
    const slats = [];
    const step = numSlats > 1 ? (end - start) / (numSlats - 1) : 0;

    for (let i = 0; i < numSlats; i++) {
        const position = start + (i * step);
        const slat = generateSlat(planesToSlice, crossSliceSelection, position);

        if (slat) {
            slats.push(slat);
        }
    }

    console.log(`Generated ${slats.length} slats`);
    return slats;
}

/**
 * Apply cap style to slice profile
 *
 * @param {SketchPath} bottomLine - Bottom line of profile
 * @param {SketchPath} topLine - Top line of profile
 * @param {number} capType - Cap type from CapType enum
 * @param {CrossSliceSelection} crossSliceSelection - Configuration
 * @private
 */
function applyCapStyle(bottomLine, topLine, capType, crossSliceSelection) {
    switch (capType) {
        case CapType.CAP_CURVE:
            applyWingedCap(bottomLine, topLine, crossSliceSelection);
            break;

        case CapType.CAP_BUTT:
            applySquareCap(bottomLine, topLine);
            break;

        case CapType.CAP_INSIDE:
            applyCutOffCap(bottomLine, topLine);
            break;

        case CapType.CAP_ROUND_SQUARE:
            applyRoundedRectangleCap(bottomLine, topLine, crossSliceSelection.cornerRadius);
            break;

        default:
            console.warn('Unknown cap type:', capType);
    }
}

/**
 * Apply winged cap (bezier curves)
 * Algorithm from SLICING_SYSTEM_SPEC.md Section 3.2 lines 517-543
 * @private
 */
function applyWingedCap(bottomLine, topLine, crossSliceSelection) {
    if (bottomLine.points.length < 2 || topLine.points.length < 2) {
        return;
    }

    // Remove first and last points to create curve gap
    const bottomFirst = bottomLine.points[0];
    const bottomSecond = bottomLine.points[1];
    bottomLine.points.shift();

    const topFirst = topLine.points[0];
    const topSecond = topLine.points[1];
    topLine.points.shift();

    // Add bezier curves between layers
    // Control points create smooth transition
    const offsetX = 20; // Offset for wing effect

    bottomFirst.controlPoint2 = new Vec2D(
        bottomSecond.x + offsetX,
        bottomSecond.y
    );

    topFirst.controlPoint2 = new Vec2D(
        topSecond.x + offsetX,
        topSecond.y
    );
}

/**
 * Apply square cap (straight edges)
 * Algorithm from SLICING_SYSTEM_SPEC.md Section 3.2 lines 545-568
 * @private
 */
function applySquareCap(bottomLine, topLine) {
    if (bottomLine.points.length < 1 || topLine.points.length < 1) {
        return;
    }

    // Explicitly remove bezier curves
    bottomLine.points[0].controlPoint1 = null;
    bottomLine.points[0].controlPoint2 = null;
    topLine.points[0].controlPoint1 = null;
    topLine.points[0].controlPoint2 = null;

    // Ends extend beyond layer boundaries with straight edges
    // No additional modifications needed - straight line connection
}

/**
 * Apply cut-off cap (flush with boundaries)
 * Algorithm from SLICING_SYSTEM_SPEC.md Section 3.2 lines 570-596
 * @private
 */
function applyCutOffCap(bottomLine, topLine) {
    // Same rendering as CAP_BUTT (straight edges)
    applySquareCap(bottomLine, topLine);

    // Different behavior flags are set at higher level:
    // - Don't add guide layers
    // - Don't remove outer planes
    // - Slots don't make edges
}

/**
 * Apply rounded rectangle cap
 * Algorithm from SLICING_SYSTEM_SPEC.md Section 3.2 lines 598-628
 * @private
 */
function applyRoundedRectangleCap(bottomLine, topLine, cornerRadius = 10) {
    if (bottomLine.points.length < 2 || topLine.points.length < 2) {
        return;
    }

    const cornerRad = cornerRadius;
    const bezierMagic = 0.552; // 4/3 * tan(π/8) - approximates circular arc

    // Add extra points at corners for bottom line
    const bottomCorner1 = bottomLine.points[0].clone();
    const bottomCorner2 = bottomLine.points[1].clone();

    bottomCorner1.y -= cornerRad;
    bottomCorner2.x += cornerRad;

    // Create 90-degree arc with bezier
    bottomCorner1.controlPoint2 = new Vec2D(
        bottomCorner1.x,
        bottomCorner1.y + cornerRad * bezierMagic
    );
    bottomCorner2.controlPoint1 = new Vec2D(
        bottomCorner2.x - cornerRad * bezierMagic,
        bottomCorner2.y
    );

    bottomLine.points.unshift(bottomCorner1);
    bottomLine.points[1] = bottomCorner2;

    // Same for top line
    const topCorner1 = topLine.points[0].clone();
    const topCorner2 = topLine.points[1].clone();

    topCorner1.y -= cornerRad;
    topCorner2.x += cornerRad;

    topCorner1.controlPoint2 = new Vec2D(
        topCorner1.x,
        topCorner1.y + cornerRad * bezierMagic
    );
    topCorner2.controlPoint1 = new Vec2D(
        topCorner2.x - cornerRad * bezierMagic,
        topCorner2.y
    );

    topLine.points.unshift(topCorner1);
    topLine.points[1] = topCorner2;
}

/**
 * Apply smooth option
 * Algorithm from GeometryOperations.java lines 1240-1243
 * @private
 */
function applySmooth(path) {
    if (!path || !path.points || path.points.length < 3) {
        return;
    }

    const smoothAmount = 0.5; // Smoothing factor

    // For each point, set control points based on neighbors
    for (let i = 1; i < path.points.length - 1; i++) {
        const prev = path.points[i - 1];
        const curr = path.points[i];
        const next = path.points[i + 1];

        // Calculate average direction
        const dirIn = new Vec2D(curr.x - prev.x, curr.y - prev.y);
        const dirOut = new Vec2D(next.x - curr.x, next.y - curr.y);

        dirIn.normalize();
        dirOut.normalize();

        const avgDir = new Vec2D(
            (dirIn.x + dirOut.x) / 2,
            (dirIn.y + dirOut.y) / 2
        ).normalize();

        // Set control points
        const controlDist = curr.distanceTo(prev) * smoothAmount;

        curr.controlPoint1 = new Vec2D(
            curr.x - avgDir.x * controlDist,
            curr.y - avgDir.y * controlDist
        );

        curr.controlPoint2 = new Vec2D(
            curr.x + avgDir.x * controlDist,
            curr.y + avgDir.y * controlDist
        );
    }
}

/**
 * Apply flush tops option
 * Algorithm from GeometryOperations.java lines 1246-1300
 * @private
 */
function applyFlushTops(topLine, materialThickness, scale) {
    if (!topLine || !topLine.points) {
        return;
    }

    const materialWidth = materialThickness / scale;

    // Double each point with offset
    for (let i = topLine.points.length - 1; i >= 0; i--) {
        const tempP = topLine.points[i];

        // Create two points offset by half material width
        const pLeft = tempP.clone();
        pLeft.x += materialWidth / 2;

        const pRight = tempP.clone();
        pRight.x -= materialWidth / 2;

        // Replace point with doubled, offset points
        topLine.points[i] = pLeft;
        topLine.points.splice(i, 0, pRight);
    }
}

/**
 * Helper: Check if path is clockwise
 * @private
 */
function isPathClockwise(path) {
    if (!path || !path.points || path.points.length < 3) {
        return false;
    }

    // Calculate signed area
    let area = 0;
    for (let i = 0; i < path.points.length; i++) {
        const p1 = path.points[i];
        const p2 = path.points[(i + 1) % path.points.length];
        area += (p2.x - p1.x) * (p2.y + p1.y);
    }

    return area > 0;
}

// Export helper functions for testing
export {
    calculateSlotLength,
    applyCapStyle,
    applyFlushTops,
    applySmooth,
    isPathClockwise
};
